import { ThemeProvider, Box, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, {
    createContext, ForwardedRef, forwardRef, Suspense,
    useContext, useEffect, useRef, useState, useMemo
} from "react";
import { useNavigate } from "react-router";
import tokenService from "../../services/tokenservice";
import { ConfirmacaoProvider } from "../confirmacao/confirmacao";
import "./browser.css";
import PageRender from "./pagerender/pagerender";
import PageBody from "./taskbar/PageBody/pagebody";
import TaskBar from "./taskbar/taskbar";
import { BrowserContextType, MenuItemType } from "./taskbar/taskbartypes.d";
import { criarTemaCustomizado, NomeTema } from "./taskbar/tema";
import { ZIndexProvider } from "./zindexcontext";

dayjs.locale('pt-br');
dayjs.extend(customParseFormat);

const EasyBrowserContext = createContext<BrowserContextType | undefined>(undefined);
export const useEasyBrowserContext = () => useContext(EasyBrowserContext);

const Browser = forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
    const pagainaDashboard: MenuItemType = {
        id: 'easy.schedule.menu.principal',
        descricao: 'Menu Principal',
        url: '/menu/menu',
        icone: "MenuIcon",
        grupo: ['principal'],
        licenca: ['GERAL']
    };

    const [listaTasks, setListaTasks]                   = useState<MenuItemType[]>([]);
    const [taskSelecionada, setTaskSelecionada]         = useState<MenuItemType | null>(null);
    const [paginasRenderizadas, setPaginasRenderizadas] = useState<any[]>([]);
    const pagesRef   = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const idsAbertos = useRef<Set<string>>(new Set());
    const [usuario, setUsuario] = useState<Record<string, any>>({});

    const [notaEntrada, setNotaEntrada] = useState<any>(null);
    const [notaSaida, setNotaSaida]     = useState<any>(null);
    const [cte, setCte]                 = useState<any>(null);

    const [configTema, setConfigTema] = useState<NomeTema>('slate');
    const temaAtualizado = useMemo(() => criarTemaCustomizado(configTema), [configTema]);
    const navigate = useNavigate();

    // ── Auth ──────────────────────────────────────────────────────────────────

    useEffect(() => {
        tokenService.getToken().then((token) => {
            if (!token) navigate("/login");
            else carregarUsuario();
        }).catch(() => navigate("/login"));
    }, []);

    // ── Visibilidade das abas ─────────────────────────────────────────────────
    //
    // Em vez de display:none (que remove a página do layout e zera o contexto
    // de z-index), movemos as páginas inativas para fora da viewport com
    // translateX(-200%).
    //
    // Vantagens:
    //   • A página continua montada e posicionada — o DOM não some
    //   • Modais/overlays de páginas inativas ficam literalmente fora da tela
    //   • Z-index global deixa de ser problema: cada página tem seu próprio
    //     contexto de empilhamento e nunca conflita com a TaskBar
    //   • Transição suave com CSS transition (opcional, ver abaixo)

    useEffect(() => {
        Object.keys(pagesRef.current).forEach((key) => {
            const el = pagesRef.current[key];
            if (!el) return;

            const ativa = key === taskSelecionada?.id;

            el.style.transform  = ativa ? 'translateX(0)'    : 'translateX(-200%)';
            el.style.position   = 'absolute';    // necessário para sobrepor sem empurrar layout
            el.style.inset      = '0';           // ocupa toda a PageBody
            el.style.visibility = ativa ? 'visible' : 'hidden'; // acessibilidade: inativas invisíveis para leitores
            el.style.pointerEvents = ativa ? 'auto' : 'none';   // impede cliques em páginas fora da tela
        });
    }, [taskSelecionada, paginasRenderizadas]);

    // ── Abre dashboard inicial ────────────────────────────────────────────────

    useEffect(() => {
        if (listaTasks.length === 0) abrirPagina(pagainaDashboard);
    }, []);

    // ── Usuário e título ──────────────────────────────────────────────────────

    const carregarUsuario = async () => {
        const user = await tokenService.getUsuario();
        if (!user) navigate("/login");
        else setUsuario(user);
        definirTitulo();
    };

    const definirTitulo = async () => {
        const empresaID = await tokenService.getEmpresaID();
        document.title = "Gestão App Codesh (" + primeiraLetraMaiuscula(empresaID) + ")";
    };

    const primeiraLetraMaiuscula = (str: string) =>
        str.charAt(0).toUpperCase() + str.slice(1);

    // ── Gerenciamento de abas ─────────────────────────────────────────────────

    const fecharPagina = (item: MenuItemType) => {
        idsAbertos.current.delete(item.id);
        const novaLista = listaTasks.filter(t => t.id !== item.id);
        setPaginasRenderizadas(prev => prev.filter(p => p.props.paginaID !== item.id));
        setListaTasks(novaLista);
        setTaskSelecionada(novaLista.at(-1) || null);
    };

    const abrirPagina = async (item: MenuItemType) => {
        const jaAberta = listaTasks.find(t => t.id === item.id);
        if (!jaAberta && !idsAbertos.current.has(item.id)) {
            idsAbertos.current.add(item.id);
            setListaTasks(prev => [...prev, item]);
            setPaginasRenderizadas(prev => [...prev, buildPagina(item)]);
        }
        setTaskSelecionada(item);
    };

    const getTask = (id: string) => listaTasks.filter(t => t.id === id);

    const buildPagina = (item: MenuItemType) => {
        const PageComponent = React.lazy(() => import("../../paginas/gestao" + item.url));
        return (
            <PageRender
                key={item.id}
                ref={(el: any) => { if (el) pagesRef.current[item.id] = el; }}
                paginaID={item.id}
                registro={item.registro}
            >
                <Suspense fallback={<Box sx={{ p: 3 }}>Carregando tela...</Box>}>
                    <ConfirmacaoProvider>
                        <ZIndexProvider>
                            <PageComponent />
                        </ZIndexProvider>
                    </ConfirmacaoProvider>
                </Suspense>
            </PageRender>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <EasyBrowserContext.Provider value={{
            listaTasks, abrirPagina, getTask, fecharPagina, taskSelecionada,
            paginasRenderizadas, usuario,
            notaEntrada, setNotaEntrada,
            notaSaida, setNotaSaida,
            cte, setCte,
            configTema, setConfigTema,
        }}>
            <ThemeProvider theme={temaAtualizado}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100vh',
                        width: '100vw',
                        overflow: 'hidden',
                        bgcolor: 'background.default',
                    }}>
                        <TaskBar />
                        <PageBody />
                    </Box>
                </LocalizationProvider>
            </ThemeProvider>
        </EasyBrowserContext.Provider>
    );
});

export default Browser;